import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Stack, styled } from "@mui/material";
import { ExitButton } from "./ExitButton";
import { LadderTitle } from "./LadderTitle";
import { LadderActivityLoading } from "./LadderActivityLoading";
import { NAV_BAR_ATTRIBUTE } from "./LadderNavBar";
import type { Ladder } from "../types";

/**
 * Floor for the scroll area, so a pathologically short window still shows a
 * sliver of the activity rather than collapsing it to nothing. Below this the
 * page scrolls again and the nav bar overlaps — which is why it stacks above
 * the Learnosity content.
 */
const MIN_SCROLL_AREA_HEIGHT = 120;

const LadderTitleSpot = styled(Box)({
  label: "LadderTitleSpot",
  position: "absolute",
  top: "19px",
  left: "110px",
  right: "110px",
  width: "70%",
});

const AssessmentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isGame",
})<{ isGame?: boolean }>(({ theme, isGame }) => ({
  label: "AssessmentContainer",
  width: "80%",
  marginTop: isGame ? "0%" : "20px",
  marginLeft: "5%",
  marginRight: "5%",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    marginTop: "30px",
    marginLeft: 0,
    marginRight: 0,
  },
}));

interface AssessmentLayoutProps {
  ladder: Ladder;
  divId: string;
  isGame?: boolean;
  loading: boolean;
  submitting: boolean;
  error: boolean;
  onExit: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemsApi: any;
  header?: React.ReactNode;
  children?: React.ReactNode;
}

export const AssessmentLayout = ({
  ladder,
  divId,
  isGame,
  loading,
  submitting,
  error,
  onExit,
  itemsApi,
  header,
  children,
}: AssessmentLayoutProps) => {
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(
    null,
  );
  // Distance from the top of the document to the top of the scroll area, plus
  // the height of the nav bar below it. 0 when there is no nav bar.
  const [reservedHeight, setReservedHeight] = useState(0);
  const observedBar = useRef<Element | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const measureRef = useRef<() => void>(() => {});

  // The nav bar is fixed to the bottom of the window, so the assessment has to
  // scroll inside whatever is left between the header and the nav bar —
  // otherwise long activities run underneath it and can't be read.
  //
  // The top is taken relative to the document, not the viewport. A
  // viewport-relative top read while the page happens to be scrolled comes back
  // too small, which makes the scroll area too tall, which pushes its bottom
  // back under the nav bar and keeps the page scrollable — a loop that leaves
  // the last stretch of the activity permanently unreachable.
  const measure = () => {
    if (!containerNode) return;
    // The nav bar renders as a sibling of the scroll area, so the shared parent
    // is enough to find it — no wrapper element needed.
    const bar =
      containerNode.parentElement?.querySelector(`[${NAV_BAR_ATTRIBUTE}]`) ??
      null;

    // Nothing to reserve until the bar mounts, and no layout is read until then.
    if (!bar) {
      observedBar.current = null;
      observer.current?.disconnect();
      observer.current = null;
      setReservedHeight(0);
      return;
    }

    // Watch the bar, since it reflows without a render — the mobile "Try Again"
    // swap, wrapping step indicators, late-loading fonts.
    if (bar !== observedBar.current) {
      observedBar.current = bar;
      observer.current?.disconnect();
      observer.current =
        typeof ResizeObserver === "undefined"
          ? null
          : new ResizeObserver(() => measureRef.current());
      observer.current?.observe(bar);
    }

    const top = containerNode.getBoundingClientRect().top + window.scrollY;
    const barHeight = bar.getBoundingClientRect().height;
    // Rounded so sub-pixel layout jitter can't retrigger this every render.
    setReservedHeight(Math.ceil(top + barHeight));
  };
  measureRef.current = measure;

  // Deliberately no dependency array. The nav bar and the header both mount
  // only once Learnosity reports ready, well after the scroll area itself, and
  // both move it — so measuring on mount alone would never find the bar and the
  // activity would go back to running underneath it.
  useLayoutEffect(() => {
    measure();
  });

  useLayoutEffect(() => {
    const onResize = () => measureRef.current();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => () => observer.current?.disconnect(), []);

  const scrollAreaSx = reservedHeight
    ? {
        maxHeight: `max(${MIN_SCROLL_AREA_HEIGHT}px, calc(100dvh - ${reservedHeight}px))`,
        overflowY: "auto" as const,
      }
    : undefined;

  const onExitButton = () => {
    if (itemsApi) {
      if (itemsApi.save) itemsApi.save();
    }
    onExit();
  };

  if (error) {
    return <div>Error loading assessment</div>;
  }

  return (
    <>
      <Stack sx={{ paddingTop: "20px", paddingLeft: "10px" }}>
        <ExitButton onClick={onExitButton} />
      </Stack>
      <LadderTitleSpot>
        <LadderTitle ladder={ladder} />
      </LadderTitleSpot>
      {header && <Box sx={{ mt: { xs: "40px", sm: 0 } }}>{header}</Box>}
      <AssessmentContainer
        ref={setContainerNode}
        isGame={isGame}
        sx={scrollAreaSx}
        className={`ladder-assessment-container ${ladder.ladderType} ladder-id-${ladder.id}`}
      >
        <div
          id={divId}
          style={{
            width: "100%",
          }}
        />
      </AssessmentContainer>
      {children}
      {(loading || submitting) && (
        <LadderActivityLoading
          label={submitting ? "Saving Results" : "Loading Assessment"}
        />
      )}
    </>
  );
};
